import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import blogPosts from "../data/blogPosts";

function pickLocale(post, lang) {
  return (post.i18n && post.i18n[lang]) || (post.i18n && post.i18n.en) || {};
}

function formatDate(iso, lang) {
  const date = new Date(iso);
  const localeMap = { en: "en-US", de: "de-DE", es: "es-ES", ka: "ka-GE" };
  return date.toLocaleDateString(localeMap[lang] || "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogIndex() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];

  const canonicalUrl = "https://ninoa.space/blog";
  const pageTitle = `${t("blog.index.title")} | Ninoa`;
  const description = t("blog.index.subtitle");

  const sortedPosts = [...blogPosts].sort(
    (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <Header />

      <main className="blog-page">
        <div className="container blog-index-inner">
          <Link to="/" className="condition-back-link">
            <span aria-hidden="true" className="condition-back-arrow">←</span>
            <span>{t("blog.back_to_ninoa")}</span>
          </Link>

          <header className="blog-index-header">
            <h1 className="blog-index-title">{t("blog.index.title")}</h1>
            <p className="blog-index-subtitle">{t("blog.index.subtitle")}</p>
          </header>

          <ul className="blog-index-list">
            {sortedPosts.map((post) => {
              const loc = pickLocale(post, lang);
              return (
                <li key={post.slug} className="blog-card">
                  <Link to={`/blog/${post.slug}`} className="blog-card-link">
                    <div className="blog-card-meta">
                      <span className="blog-card-category">{loc.category}</span>
                      <span className="blog-card-dot">·</span>
                      <span className="blog-card-date">{formatDate(post.publishedAt, lang)}</span>
                      <span className="blog-card-dot">·</span>
                      <span className="blog-card-read">
                        {post.readingTimeMin} {t("blog.min_read")}
                      </span>
                    </div>
                    <h2 className="blog-card-title">{loc.title}</h2>
                    <p className="blog-card-excerpt">{loc.excerpt}</p>
                    <span className="blog-card-cta">
                      {t("blog.read_article")} <span aria-hidden="true">→</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </main>

      <Footer />
    </>
  );
}
